/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import Cookies from 'js-cookie'
import { useOdinStore } from './odin-store'

interface OdinContextType {
  isAuthenticated: boolean
  idTokenDecoded?: Record<string, string | number | boolean>
  goToAuth: (action: 'sign-in' | 'sign-up', redirectUrl?: string) => void
}

const OdinContext = createContext<OdinContextType | undefined>(undefined)

interface OdinProviderProps extends PropsWithChildren {
  odinConfig: {
    authUrl: string
    authCookieName: string
  }
}

const REFRESH_TIMEOUT_MS = 8000

let sendRefreshRequestToIframe: (() => void) | undefined
let pendingRefresh: {
  resolve: (idToken: string) => void
  reject: (err: unknown) => void
  timer: ReturnType<typeof setTimeout>
} | null = null

/**
 * Lets code outside React (e.g. an axios interceptor) await a token refresh performed
 * by the auth app inside the hidden iframe. Only one refresh can be in flight at a time.
 */
export function requestOdinRefresh(): Promise<string> {
  if (!sendRefreshRequestToIframe) {
    return Promise.reject(new Error('Odin auth bridge is not ready'))
  }

  if (pendingRefresh) {
    return new Promise((resolve, reject) => {
      const previous = pendingRefresh
      pendingRefresh = {
        resolve: (idToken) => {
          previous?.resolve(idToken)
          resolve(idToken)
        },
        reject: (err) => {
          previous?.reject(err)
          reject(err)
        },
        timer: previous!.timer,
      }
    })
  }

  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => {
      pendingRefresh = null
      reject(new Error('Odin refresh timed out'))
    }, REFRESH_TIMEOUT_MS)

    pendingRefresh = { resolve, reject, timer }
    sendRefreshRequestToIframe?.()
  })
}

function resolvePendingRefresh(idToken: string) {
  if (!pendingRefresh) return
  clearTimeout(pendingRefresh.timer)
  pendingRefresh.resolve(idToken)
  pendingRefresh = null
}

function rejectPendingRefresh(err: unknown) {
  if (!pendingRefresh) return
  clearTimeout(pendingRefresh.timer)
  pendingRefresh.reject(err)
  pendingRefresh = null
}

export function OdinProvider({ children, odinConfig }: OdinProviderProps) {
  const { setAuthenticated, setInitialized, initialized, isAuthenticated, idTokenDecoded, resetAuth } = useOdinStore()
  const [iFrameLoaded, setIframeLoaded] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const getToken = () => {
    const idToken = Cookies.get(odinConfig.authCookieName)
    return idToken
  }

  // Check authentication status
  useEffect(() => {
    const idToken = getToken()
    if (idToken) {
      setAuthenticated(idToken)
    }
  }, [])

  const handleSendMessageToIframe = (message: any) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, odinConfig.authUrl)
    }
  }

  const handleIframeLoad = () => {
    setIframeLoaded(true)
  }

  useEffect(() => {
    if (!iFrameLoaded && !iframeRef.current) return

    sendRefreshRequestToIframe = () => handleSendMessageToIframe({ action: 'refresh_token' })

    const handleMessage = (event: MessageEvent) => {
      console.log(event)
      const msg = event.data
      console.log('Received message from iframe:', msg)
      if (msg?.type === 'exchange_token') {
        if (msg?.data?.idToken) {
          const { idToken } = msg.data
          setAuthenticated(idToken)
          resolvePendingRefresh(idToken)
        }
      } else if (msg?.type === 'sign_out') {
        resetAuth()
        rejectPendingRefresh(new Error('Odin session signed out'))
      }
    }

    window.addEventListener('message', handleMessage)
    const iframe = iframeRef.current
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad)
    }

    // Block rendering the app until the initial refresh (if any) settles, so
    // API calls don't fire against a token that's known to be stale.
    if (getToken()) {
      requestOdinRefresh().finally(() => setInitialized())
    } else {
      setInitialized()
    }

    return () => {
      window.removeEventListener('message', handleMessage)
      if (iframe) {
        iframe.removeEventListener('load', handleIframeLoad)
      }
      sendRefreshRequestToIframe = undefined
    }
  }, [iFrameLoaded, iframeRef])

  return (
    <OdinContext.Provider value={{ isAuthenticated, idTokenDecoded, goToAuth: () => console.log('TODO') }}>
      <>
        {initialized && children}
        <iframe
          ref={iframeRef}
          className="hidden"
          width="200"
          height="200"
          title="auth"
          src={`${odinConfig.authUrl}/tokens`}
          onLoad={handleIframeLoad}
        />
      </>
    </OdinContext.Provider>
  )
}

export function useOdinContext() {
  const context = useContext(OdinContext)
  if (!context) {
    throw new Error('useOdinContext must be used within an OdinProvider')
  }
  return context
}
